import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useColors } from './shared';

const LAST_UPDATED = 'April 8, 2026';

const Section = ({ title, children, c }) => (
  <Box sx={{ mb: 4 }}>
    <h2 style={{ fontFamily: "'Urbanist', sans-serif", fontWeight: 700, fontSize: '1.3rem', color: c.textPrimary, margin: '0 0 12px 0' }}>{title}</h2>
    <Box sx={{ color: c.textSecondary, fontSize: '0.95rem', lineHeight: 1.7, '& ul': { pl: 2.5, mt: 1 }, '& li': { mb: 0.5 } }}>
      {children}
    </Box>
  </Box>
);

export default function PrivacyPolicy() {
  const c = useColors();
  const navigate = useNavigate();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <Box sx={{ minHeight: '100vh', background: c.bg, py: { xs: 4, md: 8 }, px: { xs: 2, md: 4 } }}>
      <Box sx={{ maxWidth: 800, mx: 'auto' }}>
        {/* Back link */}
        <span
          onClick={() => navigate('/')}
          style={{ color: c.accent, fontSize: '0.95rem', cursor: 'pointer', fontFamily: "'Urbanist', sans-serif" }}
        >
          &larr; Back to Destrier
        </span>

        <h1 style={{ fontFamily: "'Urbanist', sans-serif", fontWeight: 800, fontSize: '2.2rem', color: c.textPrimary, margin: '24px 0 8px 0' }}>
          Privacy Policy
        </h1>
        <p style={{ color: c.textMuted, fontSize: '0.9rem', margin: '0 0 40px 0', fontFamily: "'Urbanist', sans-serif" }}>
          Last updated: {LAST_UPDATED}
        </p>

        <Section title="1. Who We Are" c={c}>
          <p>Destrier (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;) operates the website <strong>destrier.ai</strong> and provides chess analytics services. This Privacy Policy explains how we collect, use, and protect your information when you use our service.</p>
          <p style={{ marginTop: 12 }}>For any questions about this policy, contact us at <strong>destrierai@gmail.com</strong>.</p>
        </Section>

        <Section title="2. Data We Collect" c={c}>
          <p><strong>Account data</strong></p>
          <ul>
            <li>Email address (when you sign up or join the waitlist)</li>
            <li>Display name (when you set up your profile)</li>
            <li>Chess.com username (provided by you to link your account)</li>
          </ul>

          <p style={{ marginTop: 16 }}><strong>Chess game data</strong></p>
          <p>When you provide your Chess.com username, we download your publicly available game data through the Chess.com Published-Data API. This includes:</p>
          <ul>
            <li>Game results, moves, openings, and timestamps</li>
            <li>Your rating at the time of each game</li>
            <li>Opponent usernames and ratings (publicly available on Chess.com)</li>
            <li>Game URLs and time controls</li>
          </ul>
          <p style={{ marginTop: 8 }}>This data is already publicly accessible on Chess.com. We do not access any private Chess.com account information.</p>

          <p style={{ marginTop: 16 }}><strong>Usage data</strong></p>
          <ul>
            <li>Pages visited and features used (via Google Analytics 4, only after cookie consent)</li>
            <li>Training progress and statistics within Destrier</li>
          </ul>

          <p style={{ marginTop: 16 }}><strong>Technical data</strong></p>
          <ul>
            <li>IP address (used for rate limiting; not stored long-term)</li>
            <li>Browser type and device information (via Google Analytics, after consent)</li>
          </ul>
        </Section>

        <Section title="3. How We Use Your Data" c={c}>
          <ul>
            <li><strong>Provide the service</strong> — Analyze your chess games, generate insights, track your progress, and deliver training features</li>
            <li><strong>Communicate with you</strong> — Send waitlist updates and service-related emails</li>
            <li><strong>Improve the service</strong> — Understand how features are used to prioritize development (analytics data only, after consent)</li>
            <li><strong>Ensure security</strong> — Rate limiting and abuse prevention</li>
          </ul>
          <p style={{ marginTop: 12 }}>We do <strong>not</strong> sell, rent, or share your personal data with third parties for marketing purposes.</p>
        </Section>

        <Section title="4. Third-Party Services" c={c}>
          <p>We use the following third-party services to operate Destrier:</p>
          <ul>
            <li><strong>Chess.com Published-Data API</strong> — To fetch your publicly available game data. Governed by Chess.com&rsquo;s own terms of service.</li>
            <li><strong>Supabase</strong> — Authentication and database hosting. Your data is stored on Supabase infrastructure.</li>
            <li><strong>Google Analytics 4</strong> — Anonymous usage analytics, loaded only after you accept cookies.</li>
            <li><strong>Resend</strong> — Email delivery service for waitlist and transactional emails.</li>
          </ul>
          <p style={{ marginTop: 12 }}>Each of these services has its own privacy policy. We encourage you to review them.</p>
        </Section>

        <Section title="5. Opponent Data" c={c}>
          <p>When we analyze your games, we process opponent usernames and ratings that are part of the publicly available game records on Chess.com. This data is already public on Chess.com profiles and game pages. We use it solely to provide you with opponent-related analytics (e.g., performance against specific opponents, rating comparisons).</p>
          <p style={{ marginTop: 8 }}>If you are a chess player whose username appears in another user&rsquo;s game data and you have concerns, please contact us at <strong>destrierai@gmail.com</strong>.</p>
        </Section>

        <Section title="6. Cookies" c={c}>
          <p>We use cookies for the following purposes:</p>
          <ul>
            <li><strong>Essential cookies</strong> — Cookie consent preferences (stored locally)</li>
            <li><strong>Analytics cookies</strong> — Google Analytics 4 tracking (only loaded after you accept cookies via our consent banner)</li>
          </ul>
          <p style={{ marginTop: 8 }}>You can change your cookie preferences at any time by clearing your browser&rsquo;s local storage for destrier.ai.</p>
        </Section>

        <Section title="7. Data Retention" c={c}>
          <p>We retain your data for as long as you have an active account. Specifically:</p>
          <ul>
            <li><strong>Account data</strong> — Retained until you request deletion</li>
            <li><strong>Game data</strong> — Cached to provide the service; refreshed periodically from Chess.com</li>
            <li><strong>Analytics data</strong> — Retained per Google Analytics default settings (14 months)</li>
            <li><strong>Waitlist emails</strong> — Retained until the waitlist is closed or you request removal</li>
          </ul>
        </Section>

        <Section title="8. Your Rights (GDPR / CCPA)" c={c}>
          <p>Depending on your location, you may have the following rights:</p>
          <ul>
            <li><strong>Access</strong> — Request a copy of the personal data we hold about you</li>
            <li><strong>Rectification</strong> — Request correction of inaccurate data</li>
            <li><strong>Erasure</strong> — Request deletion of your personal data</li>
            <li><strong>Portability</strong> — Request your data in a machine-readable format</li>
            <li><strong>Objection</strong> — Object to processing of your data</li>
            <li><strong>Withdraw consent</strong> — Withdraw consent for analytics cookies at any time</li>
          </ul>
          <p style={{ marginTop: 12 }}>To exercise any of these rights, contact us at <strong>destrierai@gmail.com</strong>. We will respond within 30 days.</p>
        </Section>

        <Section title="9. Data Security" c={c}>
          <p>We take reasonable measures to protect your data, including:</p>
          <ul>
            <li>HTTPS encryption for all data in transit</li>
            <li>Authentication via secure tokens (Supabase Auth)</li>
            <li>Restricted CORS policy limiting API access to authorized domains</li>
            <li>Rate limiting to prevent abuse</li>
          </ul>
          <p style={{ marginTop: 8 }}>No system is 100% secure. If you discover a vulnerability, please contact us responsibly at <strong>destrierai@gmail.com</strong>.</p>
        </Section>

        <Section title="10. Changes to This Policy" c={c}>
          <p>We may update this Privacy Policy from time to time. We will notify you of significant changes by posting a notice on our website. Your continued use of the service after changes constitutes acceptance of the updated policy.</p>
        </Section>

        <Section title="11. Contact" c={c}>
          <p>For any privacy-related questions or requests:</p>
          <p style={{ marginTop: 8 }}><strong>Email:</strong> destrierai@gmail.com</p>
        </Section>

        {/* Footer */}
        <Box sx={{ mt: 6, pt: 3, borderTop: `1px solid ${c.border}`, textAlign: 'center' }}>
          <p style={{ color: c.textMuted, fontSize: '0.85rem', fontFamily: "'Urbanist', sans-serif" }}>
            &copy; {new Date().getFullYear()} Destrier. Destrier is not affiliated with, endorsed by, or sponsored by Chess.com, Lichess, or Stockfish.
          </p>
        </Box>
      </Box>
    </Box>
  );
}
